// Copyright (c) Ping. All rights reserved.
//
// Shared byte helpers for MLS conversation modules (browser + RN).

const B64_ALPHABET =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

export function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    const v = b[i] ?? 0;
    out += v.toString(16).padStart(2, '0');
  }
  return out;
}

export function hexToBytes(hex: string): Uint8Array {
  const normalized = hex.trim();
  if (normalized.length % 2 !== 0 || !/^[0-9a-fA-F]*$/.test(normalized)) {
    throw new Error('invalid hex');
  }
  const out = new Uint8Array(normalized.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(normalized.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/** True when `key` is a 32-byte MLS device id in lowercase hex (64 chars). */
export function isDeviceIdHex(key: string): boolean {
  return /^[0-9a-f]{64}$/.test(key);
}

/**
 * Canonical lowercase hex for MLS device ids. Accepts hex or BE base64url
 * `device_id` strings. Returns `null` for account ids / other labels.
 */
export function canonicalDeviceIdHex(key: string): string | null {
  const trimmed = key.trim();
  if (trimmed.length === 0) return null;
  if (isDeviceIdHex(trimmed)) return trimmed;
  if (/^[0-9a-fA-F]{64}$/.test(trimmed)) return trimmed.toLowerCase();
  // Account UUIDs and short human labels are not device ids.
  if (trimmed.includes('-')) return null;
  try {
    const bytes = base64urlToBytes(trimmed);
    if (bytes.length === 32) return bytesToHex(bytes);
  } catch {
    return null;
  }
  return null;
}

/** Encode bytes as BE `auth.EncodeB64URL` (no padding). */
export function bytesToBase64url(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  if (typeof btoa === 'function') {
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (let i = 0; i < bytes.length; i++) {
    buffer = (buffer << 8) | (bytes[i] ?? 0);
    bits += 8;
    while (bits >= 6) {
      bits -= 6;
      out += B64_ALPHABET[(buffer >> bits) & 0x3f];
    }
  }
  if (bits > 0) {
    out += B64_ALPHABET[(buffer << (6 - bits)) & 0x3f];
  }
  return out;
}

/** Decode BE `auth.EncodeB64URL` device ids and KeyPackage refs. */
export function base64urlToBytes(s: string): Uint8Array {
  const padding = '='.repeat((4 - (s.length % 4)) % 4);
  const standard = (s + padding).replace(/-/g, '+').replace(/_/g, '/');
  if (typeof atob === 'function') {
    const bin = atob(standard);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  const clean = standard.replace(/=+$/, '');
  const out: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const ch of clean) {
    const idx = B64_ALPHABET.indexOf(ch);
    if (idx < 0) continue;
    buffer = (buffer << 6) | idx;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out.push((buffer >> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
}
